import { Injectable, NgZone, inject, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { AiHelpAssistantApiService } from './ai-help-assistant-api.service';

export interface AiHelpSpeechTranscriptEvent {
  transcript: string;
  isFinal: boolean;
}

export type AiHelpVoiceEngine = 'speech-recognition' | 'media-recorder';

interface BrowserSpeechRecognition extends EventTarget {
  continuous: boolean;
  interimResults: boolean;
  lang: string;
  start(): void;
  stop(): void;
  abort(): void;
  onresult: ((event: SpeechRecognitionResultEvent) => void) | null;
  onerror: ((event: SpeechRecognitionErrorEvent) => void) | null;
  onend: (() => void) | null;
}

interface SpeechRecognitionResultEvent extends Event {
  resultIndex: number;
  results: SpeechRecognitionResultList;
}

interface SpeechRecognitionErrorEvent extends Event {
  error: string;
}

type SpeechRecognitionCtor = new () => BrowserSpeechRecognition;

const MEDIA_RECORDER_MIME_CANDIDATES = [
  'audio/webm;codecs=opus',
  'audio/webm',
  'audio/mp4',
  'audio/aac',
  'audio/ogg;codecs=opus'
] as const;

/** RMS above this counts as speech (0–1 scale from AnalyserNode). */
const SPEECH_RMS_THRESHOLD = 0.02;
/** How often to sample mic volume while recording. */
const SILENCE_POLL_MS = 100;
/** Silence after speech before auto-stop + transcribe (matches Web Speech feel). */
const SILENCE_AFTER_SPEECH_MS = 1400;
/** Give the user time to start talking before silence can end the clip. */
const INITIAL_LISTEN_GRACE_MS = 800;
/** Abort if nothing above threshold is heard. */
const NO_SPEECH_TIMEOUT_MS = 8000;
/** Hard cap so a stuck session cannot record forever. */
const MAX_RECORDING_MS = 60_000;

@Injectable({ providedIn: 'root' })
export class AiHelpSpeechRecognitionService {
  private readonly api = inject(AiHelpAssistantApiService);
  private readonly zone = inject(NgZone);

  private readonly _isSupported = signal(this.detectSupport());
  private readonly _isListening = signal(false);
  private readonly _isProcessing = signal(false);
  private readonly _engine = signal<AiHelpVoiceEngine | null>(this.resolvePreferredEngine());
  private readonly _error = signal<string | null>(null);

  readonly isSupported = this._isSupported.asReadonly();
  readonly isListening = this._isListening.asReadonly();
  readonly isProcessing = this._isProcessing.asReadonly();
  readonly engine = this._engine.asReadonly();
  readonly error = this._error.asReadonly();

  private recognition: BrowserSpeechRecognition | null = null;
  private mediaRecorder: MediaRecorder | null = null;
  private mediaStream: MediaStream | null = null;
  private audioContext: AudioContext | null = null;
  private analyser: AnalyserNode | null = null;
  private silenceMonitorId: ReturnType<typeof setInterval> | null = null;
  private recordedChunks: BlobPart[] = [];
  private recorderMimeType = '';
  private discardRecording = false;
  private captureToken = 0;
  private transcriptHandler: ((event: AiHelpSpeechTranscriptEvent) => void) | null = null;
  private endHandler: (() => void) | null = null;

  start(
    onTranscript: (event: AiHelpSpeechTranscriptEvent) => void,
    onEnd?: () => void
  ): boolean {
    if (!this._isSupported()) {
      this._error.set('Voice input is not supported in this browser.');
      return false;
    }

    this.abort();
    const token = ++this.captureToken;
    this.transcriptHandler = onTranscript;
    this.endHandler = onEnd ?? null;
    this._error.set(null);
    this.discardRecording = false;

    const preferred = this.resolvePreferredEngine();
    this._engine.set(preferred);

    if (preferred === 'speech-recognition') {
      return this.startBrowserSpeech(token);
    }

    if (preferred === 'media-recorder') {
      void this.startMediaRecorder(token);
      return true;
    }

    this._error.set('Voice input is not supported in this browser.');
    return false;
  }

  /** Finish the current capture. Media-recorder mode will transcribe the clip. */
  stop(): void {
    if (this._isProcessing()) {
      return;
    }

    if (this.recognition && this._isListening()) {
      try {
        this.recognition.stop();
      } catch {
        this.finishSession();
      }
      return;
    }

    if (this.mediaRecorder && this.mediaRecorder.state !== 'inactive') {
      try {
        this.mediaRecorder.stop();
      } catch {
        this.cleanupMediaResources();
        this.finishSession();
      }
    }
  }

  /** Cancel capture without transcribing or emitting an end callback. */
  abort(): void {
    this.captureToken += 1;
    this.discardRecording = true;
    this.endHandler = null;
    this.transcriptHandler = null;

    if (this.recognition) {
      try {
        this.recognition.abort();
      } catch {
        // Ignore abort errors when recognition is already inactive.
      }
      this.recognition = null;
    }

    const recorder = this.mediaRecorder;
    if (recorder && recorder.state !== 'inactive') {
      this._isListening.set(false);
      this._isProcessing.set(false);
      try {
        recorder.stop();
      } catch {
        this.cleanupMediaResources();
        this.discardRecording = false;
      }
      return;
    }

    this.cleanupMediaResources();
    this._isListening.set(false);
    this._isProcessing.set(false);
    this.discardRecording = false;
  }

  private startBrowserSpeech(token: number): boolean {
    const recognition = this.createRecognition();
    if (!recognition) {
      this._error.set('Voice input is not supported in this browser.');
      return false;
    }

    this.recognition = recognition;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.lang = this.resolveLanguage();

    recognition.onresult = (event) => {
      if (token !== this.captureToken) {
        return;
      }
      const transcript = this.extractTranscript(event);
      const isFinal = this.hasFinalResult(event);
      if (!transcript) {
        return;
      }
      this.transcriptHandler?.({ transcript, isFinal });
    };

    recognition.onerror = (event) => {
      if (token !== this.captureToken) {
        return;
      }
      if (event.error === 'aborted' || event.error === 'no-speech') {
        return;
      }
      this._error.set(this.mapErrorMessage(event.error));
      this.finishSession();
    };

    recognition.onend = () => {
      if (token !== this.captureToken) {
        return;
      }
      const endHandler = this.endHandler;
      this.finishSession();
      endHandler?.();
    };

    try {
      recognition.start();
      this._isListening.set(true);
      return true;
    } catch {
      this._error.set('Could not start the microphone. Please try again.');
      this.finishSession();
      return false;
    }
  }

  private async startMediaRecorder(token: number): Promise<void> {
    if (!this.canUseMediaRecorder()) {
      this._error.set('Voice input is not supported in this browser.');
      this.finishSession();
      return;
    }

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (token !== this.captureToken || this.discardRecording) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      this.mediaStream = stream;
      this.recordedChunks = [];
      this.recorderMimeType = this.pickRecorderMimeType();

      const recorder = this.recorderMimeType
        ? new MediaRecorder(stream, { mimeType: this.recorderMimeType })
        : new MediaRecorder(stream);

      this.recorderMimeType = recorder.mimeType || this.recorderMimeType;
      this.mediaRecorder = recorder;

      recorder.ondataavailable = (event: BlobEvent) => {
        if (token !== this.captureToken) {
          return;
        }
        if (event.data.size > 0) {
          this.recordedChunks.push(event.data);
        }
      };

      recorder.onerror = () => {
        if (token !== this.captureToken) {
          return;
        }
        this._error.set('Could not start the microphone. Please try again.');
        this.cleanupMediaResources();
        this.finishSession();
      };

      recorder.onstop = () => {
        void this.handleRecorderStopped(token);
      };

      recorder.start(250);
      this._isListening.set(true);
      this.startSilenceMonitor(stream, token);
    } catch (error) {
      if (token !== this.captureToken) {
        return;
      }
      this._error.set(this.mapGetUserMediaError(error));
      this.cleanupMediaResources();
      this.finishSession();
    }
  }

  /**
   * Auto-stop MediaRecorder when the user finishes speaking (silence after speech),
   * so /transcribe runs without requiring a manual pause click.
   */
  private startSilenceMonitor(stream: MediaStream, token: number): void {
    this.stopSilenceMonitor();

    if (typeof window === 'undefined' || typeof AudioContext === 'undefined') {
      return;
    }

    try {
      const AudioCtx =
        window.AudioContext ||
        (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!AudioCtx) {
        return;
      }

      const audioContext = new AudioCtx();
      const analyser = audioContext.createAnalyser();
      analyser.fftSize = 2048;
      analyser.smoothingTimeConstant = 0.8;

      const source = audioContext.createMediaStreamSource(stream);
      source.connect(analyser);

      this.audioContext = audioContext;
      this.analyser = analyser;

      void audioContext.resume().catch(() => undefined);

      const timeData = new Uint8Array(analyser.fftSize);
      const startedAt = Date.now();
      let heardSpeech = false;
      let silenceStartedAt: number | null = null;

      this.silenceMonitorId = setInterval(() => {
        if (token !== this.captureToken || !this._isListening() || this._isProcessing()) {
          this.stopSilenceMonitor();
          return;
        }

        const recorder = this.mediaRecorder;
        if (!recorder || recorder.state !== 'recording') {
          this.stopSilenceMonitor();
          return;
        }

        const elapsed = Date.now() - startedAt;
        if (elapsed >= MAX_RECORDING_MS) {
          this.stopSilenceMonitor();
          this.stop();
          return;
        }

        analyser.getByteTimeDomainData(timeData);
        const rms = this.computeRms(timeData);
        const isSpeech = rms >= SPEECH_RMS_THRESHOLD;

        if (isSpeech) {
          heardSpeech = true;
          silenceStartedAt = null;
          return;
        }

        if (!heardSpeech) {
          if (elapsed >= NO_SPEECH_TIMEOUT_MS) {
            this.stopSilenceMonitor();
            this.discardRecording = true;
            this._error.set('No speech was detected. Please try again.');
            this.stop();
          }
          return;
        }

        if (elapsed < INITIAL_LISTEN_GRACE_MS) {
          return;
        }

        if (silenceStartedAt == null) {
          silenceStartedAt = Date.now();
          return;
        }

        if (Date.now() - silenceStartedAt >= SILENCE_AFTER_SPEECH_MS) {
          this.stopSilenceMonitor();
          this.stop();
        }
      }, SILENCE_POLL_MS);
    } catch {
      // Silence monitor is best-effort; manual stop still works.
      this.stopSilenceMonitor();
    }
  }

  private stopSilenceMonitor(): void {
    if (this.silenceMonitorId != null) {
      clearInterval(this.silenceMonitorId);
      this.silenceMonitorId = null;
    }

    this.analyser = null;

    if (this.audioContext) {
      const ctx = this.audioContext;
      this.audioContext = null;
      void ctx.close().catch(() => undefined);
    }
  }

  private computeRms(timeData: Uint8Array): number {
    let sumSquares = 0;
    for (let i = 0; i < timeData.length; i += 1) {
      const normalized = (timeData[i] - 128) / 128;
      sumSquares += normalized * normalized;
    }
    return Math.sqrt(sumSquares / timeData.length);
  }

  private async handleRecorderStopped(token: number): Promise<void> {
    const chunks = this.recordedChunks;
    const mimeType = this.recorderMimeType || 'audio/webm';
    const discarded = this.discardRecording || token !== this.captureToken;
    const transcriptHandler = this.transcriptHandler;
    const endHandler = this.endHandler;

    this.cleanupMediaResources();
    this._isListening.set(false);

    if (discarded) {
      if (token === this.captureToken) {
        this.finishSession();
      }
      return;
    }

    const blob = new Blob(chunks, { type: mimeType });
    if (blob.size === 0) {
      this._error.set('No speech was detected. Please try again.');
      this.finishSession();
      return;
    }

    this._isProcessing.set(true);

    try {
      const transcript = await firstValueFrom(
        this.api.transcribe(blob, this.resolveLanguage())
      );
      if (token !== this.captureToken) {
        return;
      }

      const cleaned = transcript.trim();
      if (!cleaned) {
        this._error.set('No speech was detected. Please try again.');
        this.finishSession();
        return;
      }

      // Emit inside NgZone so the composer draft paints before auto-send.
      this.zone.run(() => {
        transcriptHandler?.({ transcript: cleaned, isFinal: true });
        this.finishSession();
        endHandler?.();
      });
    } catch {
      if (token !== this.captureToken) {
        return;
      }
      this.zone.run(() => {
        this._error.set('Could not transcribe your voice. Please try again.');
        this.finishSession();
      });
    }
  }

  private finishSession(): void {
    this._isListening.set(false);
    this._isProcessing.set(false);
    this.recognition = null;
    this.transcriptHandler = null;
    this.endHandler = null;
    this.discardRecording = false;
  }

  private cleanupMediaResources(): void {
    this.stopSilenceMonitor();
    this.mediaStream?.getTracks().forEach((track) => track.stop());
    this.mediaStream = null;
    this.mediaRecorder = null;
    this.recordedChunks = [];
  }

  private createRecognition(): BrowserSpeechRecognition | null {
    const ctor = this.getSpeechRecognitionCtor();
    return ctor ? new ctor() : null;
  }

  private detectSupport(): boolean {
    return this.resolvePreferredEngine() !== null;
  }

  private resolvePreferredEngine(): AiHelpVoiceEngine | null {
    if (this.getSpeechRecognitionCtor()) {
      return 'speech-recognition';
    }
    if (this.canUseMediaRecorder()) {
      return 'media-recorder';
    }
    return null;
  }

  private canUseMediaRecorder(): boolean {
    return (
      typeof window !== 'undefined' &&
      typeof MediaRecorder !== 'undefined' &&
      !!navigator.mediaDevices?.getUserMedia
    );
  }

  private getSpeechRecognitionCtor(): SpeechRecognitionCtor | null {
    if (typeof window === 'undefined') {
      return null;
    }
    const scope = window as Window & {
      SpeechRecognition?: SpeechRecognitionCtor;
      webkitSpeechRecognition?: SpeechRecognitionCtor;
    };
    // Prefer webkit prefix — Safari/Chrome expose it more reliably.
    return scope.webkitSpeechRecognition ?? scope.SpeechRecognition ?? null;
  }

  private pickRecorderMimeType(): string {
    if (typeof MediaRecorder === 'undefined' || typeof MediaRecorder.isTypeSupported !== 'function') {
      return '';
    }

    for (const candidate of MEDIA_RECORDER_MIME_CANDIDATES) {
      if (MediaRecorder.isTypeSupported(candidate)) {
        return candidate;
      }
    }
    return '';
  }

  private resolveLanguage(): string {
    if (typeof navigator !== 'undefined' && navigator.language) {
      return navigator.language;
    }
    return 'en-US';
  }

  private extractTranscript(event: SpeechRecognitionResultEvent): string {
    let transcript = '';
    for (let index = 0; index < event.results.length; index += 1) {
      transcript += event.results[index][0]?.transcript ?? '';
    }
    return transcript.trim();
  }

  private hasFinalResult(event: SpeechRecognitionResultEvent): boolean {
    const lastIndex = event.results.length - 1;
    return lastIndex >= 0 && event.results[lastIndex].isFinal;
  }

  private mapErrorMessage(error: string): string {
    switch (error) {
      case 'not-allowed':
      case 'service-not-allowed':
        return 'Microphone access was denied. Allow microphone permission to use voice input.';
      case 'audio-capture':
        return 'No microphone was found. Check your device and try again.';
      case 'network':
        return 'Voice input needs a network connection. Please try again.';
      default:
        return 'Voice input failed. Please try again.';
    }
  }

  private mapGetUserMediaError(error: unknown): string {
    const name =
      error && typeof error === 'object' && 'name' in error
        ? String((error as { name?: string }).name)
        : '';

    switch (name) {
      case 'NotAllowedError':
      case 'PermissionDeniedError':
        return 'Microphone access was denied. Allow microphone permission to use voice input.';
      case 'NotFoundError':
      case 'DevicesNotFoundError':
        return 'No microphone was found. Check your device and try again.';
      case 'NotReadableError':
      case 'TrackStartError':
        return 'Could not start the microphone. Please try again.';
      default:
        return 'Could not start the microphone. Please try again.';
    }
  }
}
